import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { type ActionMeta } from 'react-select';
import { useAuth0 } from '@auth0/auth0-react';
import { TrafficCone } from 'lucide-react';
import { debounce } from 'lodash';
import {
  ClearIndicator,
  CustomInput,
  CustomMultiValueRemove,
  MultiValueContainer,
} from '@/components/ui/react-select-elements';
import { shareDialogSelectClassNames } from '@/components/shares/share-dialog-select-class-names';
import { DeleteTagsCommand } from '@/hooks/commands/tags/DeleteTagsCommand';
import { useBatchEditAssets } from '@/hooks/data/assets/useBatchEditAssets';
import { useAutocompleteTags, useTagMutation } from '@/hooks/tags/useTags';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { InspectorTabs } from '@/components/inspector/inspector';
import { Label, LabelVariants } from '@/components/ui/label';
import { useCommandContext } from '@/context/CommandContext';
import { useCapabilities } from '@/hooks/useCapabilities';
import { type Asset, type AssetTag } from '@/types/asset';
import { ObjectAction, ObjectType } from '@/types/batch';
import { useTreeContext } from '@/context/TreeContext';
import { type Option } from '@/types/select-option';
import { cn, getUploadCsvKey } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';

/**
 * `id` is set only for a known existing tag; `value` uses it when present so two
 * same-named tags stay independently selectable. `parentKnown` marks whether `parent`
 * is authoritative (`null` meaning root) — an upload draft leaves it unset.
 */
export type TagOption = Option & { parent?: string | null; parentKnown?: boolean; id?: string; name: string };

/** Disambiguates same-named tags filed under different parents in the suggestion list. */
export const formatTagSuggestionLabel = (tag: { name: string; parentName: string | null }): string =>
  tag.parentName ? `${tag.parentName} - ${tag.name}` : tag.name;

/**
 * Splits into `tags` (id-less names, resolved/created by name) and `tagIds`
 * (options already known by ID) — exclusive, so a same-named tag under
 * another parent can't get redundantly re-matched by name.
 */
export const splitTagOptionsForSave = (
  options: ReadonlyArray<TagOption>,
): { tags: Array<string>; tagIds: Array<string> } => ({
  tags: options.filter((tag) => !tag.id).map((tag) => tag.name),
  tagIds: options.flatMap((tag) => (tag.id ? [tag.id] : [])),
});

/**
 * Shared fields for a tag whose parent is actually known — used by toTagOption() and
 * the suggestion list below, whose labels differ. `value` falls back to `name` only
 * when no id is known, so two same-named tags stay independently selectable.
 */
const tagOptionFields = (tag: { id?: string; name: string; parent: string | null }): Omit<TagOption, 'label'> => ({
  value: tag.id ?? tag.name,
  parent: tag.parent,
  parentKnown: true,
  name: tag.name,
  id: tag.id,
});

export const toTagOption = (tag: AssetTag): TagOption => ({ label: tag.name, ...tagOptionFields(tag) });

/**
 * Hides a suggestion already selected. IDs disambiguate when both sides have one; an
 * upload draft tag never has parentKnown set, so it falls back to a name-only match.
 */
export const isTagOptionSelected = (option: TagOption, selectedValues: ReadonlyArray<TagOption>): boolean =>
  selectedValues.some((selected) => {
    if (option.id && selected.id) {
      return option.id === selected.id;
    }
    if (!selected.parentKnown) {
      return option.name === selected.name;
    }
    return option.name === selected.name && option.parent === selected.parent;
  });

export const TagsAccordion: FC<{
  asset?: Asset;
  multipleAssetsTags?: { isFullIntersection: boolean; tags: Array<AssetTag> };
  disabled?: boolean;
  selectedItems?: Array<{ id: string; name: string }>;
  folderId?: string;
  albumId?: string;
  readOnly?: boolean;
  currentInspectorTab: InspectorTabs;
  isUpload?: boolean;
}> = ({
  asset,
  multipleAssetsTags,
  disabled,
  selectedItems,
  folderId,
  albumId,
  readOnly = false,
  currentInspectorTab,
  isUpload,
}) => {
  const portalTarget = document.querySelector('#portal');

  const [values, setValues] = useState<Array<TagOption> | []>([]);
  const [inputValue, setInputValue] = useState('');
  const [uploadsIndeterminate, setUploadsIndeterminate] = useState(false);

  const tTags = useTranslations('inspector.tags');
  const { apply } = useCommandContext();
  const { isAuthenticated } = useAuth0();
  const { canViewPrivateTabs } = useCapabilities();
  const { batchEditAssetsMutation } = useBatchEditAssets();
  const { updateAssetMutation } = useTagMutation();
  const { includeSubFolderAssets } = useTreeContext();
  const { getActiveUploadSession, setManualMetadata } = useUploadStore();
  const { uploadSessionId, session } = getActiveUploadSession();
  const { selectedAssets: uploadSelectedAssetsIds, manualMetadata, csvData } = session;

  useEffect(() => {
    if (isUpload) {
      if (uploadSelectedAssetsIds?.length > 0) {
        const firstTags =
          manualMetadata[uploadSelectedAssetsIds[0].id]?.tags ??
          csvData[getUploadCsvKey(uploadSelectedAssetsIds[0])]?.tags ??
          [];

        const allMatch = uploadSelectedAssetsIds.every(({ id, name, originalName }) => {
          const assetTags = manualMetadata[id]?.tags ?? csvData[getUploadCsvKey({ name, originalName })]?.tags ?? [];
          return assetTags.length === firstTags.length && assetTags.every((tag) => firstTags.includes(tag));
        });

        if (allMatch) {
          setValues(firstTags.map((tag) => ({ label: tag, value: tag, name: tag })));
          setUploadsIndeterminate(false);
        } else {
          setValues([]);
          setUploadsIndeterminate(true);
        }
      } else {
        setValues([]);
        setUploadsIndeterminate(false);
      }
    } else {
      if (multipleAssetsTags) {
        setValues(multipleAssetsTags.tags.map(toTagOption));
      } else if (asset?.tags) {
        setValues(asset.tags.map(toTagOption));
      }
    }
  }, [isUpload, asset, multipleAssetsTags, uploadSelectedAssetsIds, manualMetadata]);

  const debounceInputChange = useCallback(
    debounce((value) => {
      setInputValue(value);
    }, 300),
    [],
  );

  // Shared tags across a multi-selection that don't fully intersect are shown in
  // the "indeterminate" (yellow) style — same as the editable select below.
  const isIndeterminate =
    (Boolean(multipleAssetsTags) && !multipleAssetsTags?.isFullIntersection) || (isUpload && uploadsIndeterminate);

  // Which Badge variant each half of a parent/child tag pill uses. Resolved once and shared by
  // the read-only Badge pair below and the editable chip's MultiValueContainer.
  const parentVariant = isIndeterminate ? 'warningDark' : 'medium';
  const childVariant = isIndeterminate ? 'warning' : 'light';

  const handleInputChange = (newValue: string) => {
    if (newValue.length >= 1) {
      debounceInputChange(newValue);
    } else {
      setInputValue('');
    }
  };

  const { tags, areTagsFetching } = useAutocompleteTags(
    { q: inputValue },
    {
      enabled:
        Boolean(inputValue) &&
        inputValue.length >= 1 &&
        currentInspectorTab === InspectorTabs.Information &&
        isAuthenticated,
    },
  );

  const handleCreate = async (newTag: string) => {
    const { tags: existingNames, tagIds } = splitTagOptionsForSave(values);
    const newValues = [...existingNames, newTag];
    if (isUpload) {
      uploadSelectedAssetsIds.forEach((selectedAsset) => {
        setManualMetadata(
          uploadSessionId,
          {
            tags: newValues,
          },
          [
            {
              id: selectedAsset.id,
              name: selectedAsset.name,
              originalName: selectedAsset.originalName,
            },
          ],
          true,
        );
      });
    } else if (multipleAssetsTags) {
      batchEditAssetsMutation.mutate({
        objectType: ObjectType.Asset,
        objectAction: ObjectAction.Update,
        filter: {
          ...(selectedItems && { assetIds: selectedItems.map((selectedAsset) => selectedAsset.id) }),
          ...(folderId && { folderId, includeSubFolderAssets }),
          ...(albumId && { albumId }),
        },
        payload: {
          tags: newValues,
          tagIds,
        },
      });
    } else {
      await updateAssetMutation({
        id: asset?.id as string,
        body: { tags: newValues, tagIds },
      });
    }
  };

  const onChange = async (newOptions: ReadonlyArray<TagOption>, context: ActionMeta<TagOption>) => {
    const { tags: newValues, tagIds: newTagIds } = splitTagOptionsForSave(newOptions);
    if (isUpload) {
      uploadSelectedAssetsIds.forEach((selectedAsset) => {
        setManualMetadata(
          uploadSessionId,
          {
            tags: newValues,
          },
          [
            {
              id: selectedAsset.id,
              name: selectedAsset.name,
              originalName: selectedAsset.originalName,
            },
          ],
          true,
        );
      });
    } else if (multipleAssetsTags) {
      batchEditAssetsMutation.mutate({
        objectType: ObjectType.Asset,
        objectAction: ObjectAction.Update,
        filter: {
          ...(selectedItems && { assetIds: selectedItems.map((selectedAsset) => selectedAsset.id) }),
          ...(folderId && { folderId, includeSubFolderAssets }),
          ...(albumId && { albumId }),
        },
        payload: {
          tags: newValues,
          tagIds: newTagIds,
        },
      });
    } else {
      if (context.action === 'clear') {
        const { tags: oldTags, tagIds: oldTagIds } = splitTagOptionsForSave(values);

        const deleteCommand = DeleteTagsCommand(
          asset?.id as string,
          oldTags,
          oldTagIds,
          newValues,
          newTagIds,
          updateAssetMutation,
        );
        apply(deleteCommand);
      } else {
        await updateAssetMutation({
          id: asset?.id as string,
          body: { tags: newValues, tagIds: newTagIds },
        });
        setInputValue('');
      }
    }
  };

  // react-select treats each `components` entry as a component type, not a prop value — a new
  // function identity here unmounts and remounts every chip on every render (e.g. each keystroke
  // in the input, since that updates `inputValue`/`debounceInputChange` state). Memoizing the
  // whole object keeps those identities stable across renders that don't change the tone.
  const selectComponents = useMemo(
    () => ({
      Input: CustomInput,
      DropdownIndicator: () => null,
      ClearIndicator,
      MultiValueContainer: (props: any) => (
        <MultiValueContainer
          {...props}
          parentVariant={parentVariant}
          childVariant={childVariant}
          isIndeterminate={isIndeterminate}
        />
      ),
      MultiValueRemove: (props: any) => <CustomMultiValueRemove {...props} isIndeterminate={isIndeterminate} />,
      IndicatorSeparator: () => null,
    }),
    [parentVariant, childVariant, isIndeterminate],
  );

  return !canViewPrivateTabs && values.length === 0 ? (
    <div className="flex flex-col p-3">
      <Label variant={LabelVariants.INSPECTOR_SECTION}>{tTags('title')}</Label>
      <div className="py-8 text-center text-text-gray-alternate">
        <TrafficCone className="mx-auto mb-2" size={28} strokeWidth={1.5} />
        {tTags('empty')}
      </div>
    </div>
  ) : (
    <div className="flex flex-col p-3">
      <Label variant={LabelVariants.INSPECTOR_SECTION}>{tTags('title')}</Label>
      {readOnly ? (
        <div className="flex flex-wrap gap-1">
          {values.map((tag) =>
            tag.parent ? (
              <Badge
                className="max-w-full min-w-0 text-xs! wrap-anywhere"
                valueClassName={cn('max-w-full min-w-0 wrap-anywhere text-xs!', isIndeterminate && 'text-black')}
                key={tag.value}
                variant={parentVariant}
                valueVariant={childVariant}
                value={tag.name}
              >
                {tag.parent}
              </Badge>
            ) : (
              <Badge
                className={cn(
                  'max-w-full min-w-0 wrap-anywhere text-xs!',
                  isIndeterminate ? 'text-black' : 'text-text-gray',
                )}
                valueClassName="text-xs!"
                key={tag.value}
                variant={childVariant}
              >
                {tag.name}
              </Badge>
            ),
          )}
        </div>
      ) : (
        <CreatableSelect<TagOption, true>
          isClearable
          isMulti
          options={(tags?.suggestions ?? []).map((tag) => ({
            label: formatTagSuggestionLabel(tag),
            ...tagOptionFields({ id: tag.id, name: tag.name, parent: tag.parentName }),
          }))}
          onChange={onChange}
          placeholder={isIndeterminate ? tTags('indeterminatePlaceholder') : tTags('searchPlaceholder')}
          value={values}
          unstyled
          // Custom prop read back via `selectProps` in shareDialogSelectClassNames.
          {...({ indeterminate: isIndeterminate } as any)}
          menuPortalTarget={portalTarget instanceof HTMLElement && portalTarget}
          filterOption={null}
          isOptionSelected={isTagOptionSelected}
          isDisabled={disabled}
          isLoading={areTagsFetching}
          onCreateOption={handleCreate}
          onInputChange={handleInputChange}
          components={selectComponents}
          classNames={shareDialogSelectClassNames as any}
        />
      )}
    </div>
  );
};
